import {
  Home, User, IndianRupee, CalendarClock, CheckCircle2, ClipboardCheck, CalendarDays, Wrench,
  Hammer, Clock, Receipt, FolderOpen, Camera,
} from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const TILES = [
  { Icon: Home, label: "Property Status", value: "Occupied", ok: true },
  { Icon: User, label: "Current Tenant", value: "On record" },
  { Icon: IndianRupee, label: "Monthly Rent", value: "Tracked" },
  { Icon: CalendarClock, label: "Rent Due", value: "Monitored" },
  { Icon: CheckCircle2, label: "Rent Received", value: "Updated", ok: true },
  { Icon: ClipboardCheck, label: "Last Inspection", value: "Completed" },
  { Icon: CalendarDays, label: "Next Inspection", value: "Scheduled" },
  { Icon: Wrench, label: "Open Maintenance", value: "Logged" },
  { Icon: Hammer, label: "Completed Work", value: "Documented" },
  { Icon: Clock, label: "Pending Approvals", value: "With owner" },
  { Icon: Receipt, label: "Expenses", value: "Itemised" },
  { Icon: FolderOpen, label: "Documents", value: "Stored" },
  { Icon: Camera, label: "Photos & Videos", value: "On file" },
];

export const DashboardPreview = () => (
  <section className="relative overflow-hidden bg-navy-950 py-24 sm:py-32" data-testid="dashboard-preview-section">
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: "radial-gradient(ellipse 55% 45% at 50% 0%, rgba(201,162,39,0.09), transparent 60%)" }}
      aria-hidden="true"
    />
    <div className="container-site relative">
      <SectionHeading
        chapter="07"
        eyebrow="Owner Reporting"
        dark
        align="center"
        title="Complete Visibility. Even From Another Country."
        copy="Property status, tenant, rent, inspections, maintenance, approvals, expenses, documents, photos and videos — organised around your property, not scattered across chats."
      />

      <Reveal delay={0.15}>
        <div className="mx-auto mt-14 max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-navy-800/50 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)] backdrop-blur-xl" data-testid="dashboard-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-6 py-4 sm:px-8">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold-500 font-display text-[10px] font-extrabold text-navy-900">NRI</span>
              <div>
                <p className="font-display text-sm font-bold text-white">Your Property — Hyderabad</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Owner dashboard</p>
              </div>
            </div>
            <span className="flex items-center gap-2 rounded-full border border-status/30 bg-status/10 px-3.5 py-1.5 text-[11px] font-bold text-status">
              <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-status" /> All clear
            </span>
          </div>
          <Stagger className="grid grid-cols-2 gap-px bg-white/5 sm:grid-cols-3 lg:grid-cols-4" gap={0.04}>
            {TILES.map(({ Icon, label, value, ok }) => (
              <StaggerItem key={label} className="bg-navy-900/80">
                <div className="flex h-full flex-col gap-2.5 p-5 sm:p-6">
                  <div className="flex items-center justify-between">
                    <Icon className={`h-4 w-4 ${ok ? "text-status" : "text-gold-400/80"}`} />
                    {ok && <span className="h-1.5 w-1.5 rounded-full bg-status" aria-hidden="true" />}
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{label}</p>
                  <p className="font-display text-sm font-bold text-white sm:text-base">{value}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </Reveal>

      <Reveal delay={0.25}>
        <p className="mx-auto mt-8 max-w-2xl text-center text-xs leading-relaxed text-white/40 sm:text-sm">
          Conceptual preview. Version 1 delivers this visibility through structured WhatsApp and email
          reporting — a full owner portal and mobile app are on the roadmap, built on the same backend.
        </p>
      </Reveal>
    </div>
  </section>
);
